import MapboxLayer from '@planet/maps/layer/MapboxVector.js';
import PLMap from '@planet/maps/Map.js';
import React, {useEffect, useRef, useState} from 'react';
import TileLayer from '@planet/maps/layer/Tile.js';
import XYZSource from '@planet/maps/source/XYZ.js';
import {Box, ThemeProvider, useTheme} from '@mui/material';
import {bool, func, node, object, oneOf, oneOfType} from 'prop-types';
import {defaults as defaultInteractions} from 'ol/interaction/defaults.js';

import LayerSwitcher from './LayerSwitcher.jsx';
import ZoomControls from './ZoomControls.jsx';
import {
  BASE_LAYERS,
  BASE_LAYERS_STYLES,
  DEFAULT_ZOOM,
  MAX_ZOOM,
} from './constants.js';
import {MapThemeProvider} from './context/MapThemeProvider.jsx';
import {useMapAuth} from './context/MapAuthProvider.jsx';
import blueGrey  from '@mui/material/colors/blueGrey.js';

const Map = ({
  allowInteractions = true,
  baseLayer = BASE_LAYERS.DARK,
  children,
  labelsChecked = true,
  mapRef,
  onBaseLayerChange,
  onLabelsChange,
  onZoomToFeatures,
  showLayerSwitcher = true,
  showMapControls = true,
  ...props
}) => {
  const {mapboxAccessToken, authToken, authTarget} = useMapAuth();
  const [selectedBaseLayer, setSelectedBaseLayer] = useState(baseLayer);
  const [labelsOn, setLabelsOn] = useState(labelsChecked);
  const theme = useTheme();

  const olMap = useRef(null);

  const zoomByDelta = delta => {
    const view = olMap.current.getView();
    const currentZoom = view.getZoom();
    const newZoom = view.getConstrainedZoom(currentZoom + delta);

    view.setZoom(newZoom);
  };

  const zoomIn = () => {
    zoomByDelta(1);
  };

  const zoomOut = () => {
    zoomByDelta(-1);
  };

  useEffect(() => {
    if (olMap?.current) {
      // center the map and set default zoom to get
      // animations working without a controlled view
      const mapView = olMap.current.getView();
      mapView.setCenter([0, 0]);
      mapView.setZoom(DEFAULT_ZOOM);
      mapView.setMinZoom(DEFAULT_ZOOM);
    }
  }, [olMap]);

  useEffect(() => {
    setLabelsOn(labelsChecked);
  }, [labelsChecked]);

  useEffect(() => {
    setSelectedBaseLayer(baseLayer);
  }, [baseLayer]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: blueGrey[900],
      }}
    >
      <PLMap
        ref={node => {
          olMap.current = node;

          if (mapRef) {
            if (typeof ref === 'function') {
              mapRef(node);
            } else if (mapRef) {
              mapRef.current = node;
            }
          }
        }}
        controls={[]}
        interactions={allowInteractions ? defaultInteractions() : []}
        {...props}
      >
        <ThemeProvider theme={theme}>
          <MapThemeProvider>
            <TileLayer visible={selectedBaseLayer === BASE_LAYERS.SATELLITE}>
              <XYZSource
                url={`${BASE_LAYERS_STYLES[BASE_LAYERS.SATELLITE].background(
                  authTarget,
                  authToken,
                )}`}
                options={{
                  crossOrigin: 'anonymous',
                  attributions: ['©Planet Labs PBC'],
                }}
              />
            </TileLayer>
            <MapboxLayer
              visible={selectedBaseLayer === BASE_LAYERS.DARK}
              options={{
                accessToken: mapboxAccessToken,
                styleUrl: BASE_LAYERS_STYLES[BASE_LAYERS.DARK].background,
              }}
            />
            <MapboxLayer
              visible={selectedBaseLayer === BASE_LAYERS.LIGHT}
              options={{
                accessToken: mapboxAccessToken,
                styleUrl: BASE_LAYERS_STYLES[BASE_LAYERS.LIGHT].background,
              }}
            />
            <MapboxLayer
              visible={labelsOn}
              key={`labels_${selectedBaseLayer}`}
              options={{
                accessToken: mapboxAccessToken,
                styleUrl: BASE_LAYERS_STYLES[selectedBaseLayer].labels,
              }}
            />

            {children}
          </MapThemeProvider>
        </ThemeProvider>
      </PLMap>
      {showMapControls && (
        <Box
          sx={{
            position: 'absolute',
            right: 4,
            bottom: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              maxWidth: 'inherit',
              position: 'absolute',
              bottom: theme => theme.spacing(1),
              right: theme => theme.spacing(1),
            }}
          >
            {showLayerSwitcher && (
              <LayerSwitcher
                selectedBackground={selectedBaseLayer}
                onBackgroundChange={newValue => {
                  setSelectedBaseLayer(newValue);
                  if (onBaseLayerChange) {
                    onBaseLayerChange(newValue);
                  }
                }}
                labelsOn={labelsOn}
                onLabelsChange={checked => {
                  setLabelsOn(checked);
                  if (onLabelsChange) {
                    onLabelsChange(checked);
                  }
                }}
                sx={{
                  marginBottom: 1,
                }}
              />
            )}
            <ZoomControls
              maxZoom={MAX_ZOOM}
              minZoom={DEFAULT_ZOOM}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              onZoomToFeatures={onZoomToFeatures}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

Map.propTypes = {
  /**
   * If `true`, allow default map interactions
   */
  allowInteractions: bool,

  /**
   * Additional map layers
   */
  children: node,

  /**
   * If `true`, display zoom controls
   */
  showMapControls: bool,

  onZoomToFeatures: func,

  /**
   * Base layer to display
   */
  baseLayer: oneOf([
    BASE_LAYERS.DARK,
    BASE_LAYERS.LIGHT,
    BASE_LAYERS.SATELLITE,
  ]),

  /**
   * function to control the layer switcher selections
   */
  onBaseLayerChange: func,

  /**
   * If `true`, labels will be displayed. Default: `true`
   */
  labelsChecked: bool,

  onLabelsChange: func,

  mapRef: oneOfType([func, object]),

  /**
   * if `true`, layer switcher will be added
   */
  showLayerSwitcher: bool,
};

export default Map;
